import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageBodyClass from "../utils/usePageBodyClass";
import ExamProctorWidget from "../components/ExamProctorWidget";
import axios from "axios"; // NEW: Import Axios
import "./Dashboard.css";

const $ = window.$;

export default function Dashboard() {
  usePageBodyClass("page-dashboard");
  const navigate = useNavigate();
  
  // States
  const [showWarning, setShowWarning] = useState(false);
  const [examQuestions, setExamQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // State refs for mutable exam tracking
  const [userAnswers, setUserAnswers] = useState({});
  const [questionStates, setQuestionStates] = useState({});
  const [lockedQuestions, setLockedQuestions] = useState({});

  useEffect(() => {
    // ==========================================
    // 1. FETCH FROM MONGODB
    // ==========================================
    const fetchQuestions = async () => {
      try {
        const response = await axios.get("http://localhost:5000/api/exams/questions");
        const questionsFromDB = response.data;
        
        if (questionsFromDB.length === 0) {
          alert("No active exam questions found in database.");
          navigate("/login", { replace: true });
          return;
        }

        setExamQuestions(questionsFromDB);
        
        // Initialize states
        const initialStates = {};
        questionsFromDB.forEach((_, idx) => (initialStates[idx] = "not-visited"));
        setQuestionStates(initialStates);
        setIsLoading(false);

      } catch (error) {
        console.error("Error fetching from MongoDB:", error);
        alert("Database connection error. Please contact administrator.");
      }
    };

    fetchQuestions();

    // ==========================================
    // 2. EXAM PROCTORING & SECURITY
    // ==========================================
    const candName = localStorage.getItem("candidateName") || "Guest Candidate";
    $("#candidateDisplay").text(candName);

    const clockTimer = setInterval(() => {
      const now = new Date();
      $("#liveDateTime").text(
        now.toLocaleDateString("en-US", {
          weekday: "short", year: "numeric", month: "short",
          day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
        })
      );
    }, 1000);

    $(document).on("selectstart.pageDash copy.pageDash paste.pageDash", (e) => e.preventDefault());

    let examSubmitted = false;
    let tabSwitchCount = 0;

    $(document).on("visibilitychange.pageDash", function () {
      if (document.hidden && localStorage.getItem("examStatus") === "locked") {
        tabSwitchCount++;
        if (tabSwitchCount >= 2) {
          alert("VIOLATION LIMIT EXCEEDED: Tab switching detected. Auto-submitting exam!");
          submitExamToMongoDB(); // Call MongoDB submit
        } else {
          alert(`WARNING (${tabSwitchCount}/2): Switching windows is forbidden!`);
        }
      }
    });

    let closeAttempted = false;
    const handleBeforeUnload = (e) => {
      if (examSubmitted) return;
      closeAttempted = true;
      e.preventDefault();
      e.returnValue = ""; 
    };

    const handleWindowFocus = () => {
      if (closeAttempted) {
        closeAttempted = false;
        setShowWarning(true);
      }
    };

    const handleFullscreenChange = () => {
      if (examSubmitted) return;
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setShowWarning(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    // Floating Calculator
    $(".calc-btn").on("click.pageDash", function () {
      const val = $(this).data("val");
      const screen = $("#calcDisplay");
      if (val === "C") screen.text("0");
      else if (val === "=") {
        try { screen.text(eval(screen.text())); } catch (err) { screen.text("Error"); }
      } else screen.text(screen.text() === "0" || screen.text() === "Error" ? val : screen.text() + val);
    });

    return () => {
      clearInterval(clockTimer);
      $(document).off(".pageDash");
      $(".calc-btn").off(".pageDash");
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [navigate]);

  // ==========================================
  // 3. SUBMIT TO MONGODB
  // ==========================================
  const submitExamToMongoDB = async () => {
    try {
      const payload = {
        candidateId: "cand_" + Date.now(), // Generate or grab from localStorage
        candidateName: localStorage.getItem("candidateName"),
        email: localStorage.getItem("candidateEmail"),
        phone: localStorage.getItem("candidatePhone"),
        userAnswers: userAnswers
      };

      // Send to Backend
      await axios.post("http://localhost:5000/api/exams/submit", payload);
      
      // Save minimal data for summary page locally
      localStorage.setItem("examSummaryData", JSON.stringify({
        questions: examQuestions,
        userAnswers: userAnswers,
      }));

      navigate("/summary", { replace: true });
    } catch (error) {
      console.error("Error submitting to MongoDB:", error);
      alert("Failed to submit exam. Please call the proctor.");
    }
  };

  // UI Handlers
  const handleOptionChange = (qIndex, oIndex) => {
    if (lockedQuestions[qIndex]) return;
    setUserAnswers({ ...userAnswers, [qIndex]: oIndex });
    setQuestionStates({ ...questionStates, [qIndex]: "answered" });
  };

  const handleSave = () => {
    if (userAnswers[currentIndex] !== undefined) {
      setLockedQuestions({ ...lockedQuestions, [currentIndex]: true });
      setQuestionStates({ ...questionStates, [currentIndex]: "answered" });
    }
  };

  const handleClear = () => {
    if (lockedQuestions[currentIndex]) return;
    const newAnswers = { ...userAnswers };
    delete newAnswers[currentIndex];
    setUserAnswers(newAnswers);
    setQuestionStates({ ...questionStates, [currentIndex]: "not-answered" });
  };

  const handleReviewAndNext = () => {
    setQuestionStates({ ...questionStates, [currentIndex]: "marked" });
    if (currentIndex < examQuestions.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handleConfirmSubmit = () => {
    const modalEl = document.getElementById("confirmSubmitModal");
    if (window.bootstrap && modalEl) {
      const instance = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
      instance.hide();
    }
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

    submitExamToMongoDB();
  };

  if (isLoading) {
    return <div className="text-center mt-5 text-white"><h2>Loading Secure Exam Environment...</h2></div>;
  }

  const currentQ = examQuestions[currentIndex];
  const isLocked = !!lockedQuestions[currentIndex];

  // Derive counts
  const counts = { answered: 0, notAnswered: 0, marked: 0, notVisited: 0 };
  examQuestions.forEach((_, idx) => {
    const st = questionStates[idx];
    if (st === "answered") counts.answered++;
    else if (st === "not-answered") counts.notAnswered++;
    else if (st === "marked") counts.marked++;
    else counts.notVisited++;
  });

  return (
    <div className="page-dashboard">
      <ExamProctorWidget />

      {/* Top Window Switch Guard */}
      <div className="top-hover-guard">
        <button className="mock-close-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowWarning(true); }}>
          <i className="fa-solid fa-xmark me-2"></i>Switch Window
        </button>
      </div>

      {/* In-App Fullscreen Warning Modal */}
      {showWarning && (
        <div className="custom-warning-overlay">
          <div className="custom-warning-box">
            <i className="fa-solid fa-triangle-exclamation fa-3x text-danger mb-3"></i>
            <h4 className="fw-bold mb-2">Action Denied</h4>
            <p className="text-secondary fs-5 mb-4">You should immediately continue the exam or it will be terminated.</p>
            <button className="btn btn-danger px-4 fw-bold" onClick={() => {
              setShowWarning(false);
              if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen().catch((err) => console.log(err));
              }
            }}>
              Return to Exam
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="top-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <h5 className="mb-0 text-warning"><i className="fa-solid fa-graduation-cap me-2"></i>Online Exam Portal</h5>
          <span className="badge bg-secondary" id="candidateDisplay">Candidate</span>
        </div>
        <div className="header-datetime d-none d-md-flex align-items-center">
          <i className="fa-regular fa-calendar-days me-2 text-warning"></i>
          <span id="liveDateTime">Loading Date &amp; Time...</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-outline-light btn-sm" data-bs-toggle="modal" data-bs-target="#calculatorModal">
            <i className="fa-solid fa-calculator me-1"></i> Calculator
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-3">
        <div className="row">
          <div className="col-lg-9">
            <div className="question-card shadow-sm">
              <div>
                <div className="question-header d-flex justify-content-between align-items-center">
                  <span>Question No. <span>{currentIndex + 1}</span></span>
                  <span className="badge bg-primary px-3 py-2">Marks: +1, -0</span>
                </div>
                <div className="p-4">
                  <h5 className="fw-semibold mb-3">{currentQ.text}</h5>
                  {isLocked && (
                    <div className="alert alert-secondary py-2 px-3 mb-3 small">
                      <i className="fa-solid fa-lock me-2 text-warning"></i>
                      <strong>Response Locked:</strong> Answers cannot be modified.
                    </div>
                  )}
                  <div>
                    {currentQ.options.map((opt, oIdx) => (
                      <label key={oIdx} className={`option-label ${isLocked ? "locked-option" : ""}`}>
                        <input
                          type="radio"
                          className="form-check-input me-3"
                          name="mcqOption"
                          value={oIdx}
                          checked={userAnswers[currentIndex] === oIdx}
                          disabled={isLocked}
                          onChange={() => handleOptionChange(currentIndex, oIdx)}
                        />
                        <span className="option-text">{String.fromCharCode(65 + oIdx)}. {opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="bg-light p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <button className="btn btn-outline-secondary me-2" onClick={handleClear} disabled={isLocked}>
                    <i className="fa-solid fa-rotate-left me-1"></i> Clear Response
                  </button>
                  <button className="btn btn-warning text-dark" onClick={handleReviewAndNext}>
                    <i className="fa-solid fa-bookmark me-1"></i> Mark for Review &amp; Next
                  </button>
                </div>
                <div>
                  <button className="btn btn-success px-4 fw-bold me-2" onClick={handleSave}>
                    <i className="fa-solid fa-floppy-disk me-1"></i> Save
                  </button>
                  <button className="btn btn-primary px-4 fw-bold" onClick={() => currentIndex < examQuestions.length - 1 && setCurrentIndex(currentIndex + 1)}>
                    Next <i className="fa-solid fa-chevron-right ms-1"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Palette */}
          <div className="col-lg-3 mt-3 mt-lg-0">
            <div className="palette-card shadow-sm">
              <h6 className="fw-bold mb-3 border-bottom pb-2"><i className="fa-solid fa-grid-2 me-2"></i>Question Palette</h6>
              <div className="row g-2 mb-3 small">
                <div className="col-6"><span className="status-badge bg-success">{counts.answered}</span> Answered</div>
                <div className="col-6"><span className="status-badge bg-danger">{counts.notAnswered}</span> Not Answered</div>
                <div className="col-6"><span className="status-badge bg-purple" style={{ background: "#6f42c1" }}>{counts.marked}</span> Review</div>
                <div className="col-6"><span className="status-badge bg-secondary">{counts.notVisited}</span> Not Visited</div>
              </div>
              <hr />
              <div className="d-flex flex-wrap justify-content-start">
                {examQuestions.map((_, idx) => {
                  let cls = "not-visited";
                  if (questionStates[idx] === "answered") cls = "answered";
                  if (questionStates[idx] === "not-answered") cls = "not-answered";
                  if (questionStates[idx] === "marked") cls = "marked";
                  if (idx === currentIndex) cls += " active-q";
                  return (
                    <button key={idx} className={`q-btn ${cls}`} onClick={() => {
                        setCurrentIndex(idx);
                        if(questionStates[idx] === "not-visited") {
                            setQuestionStates({...questionStates, [idx]: "not-answered"});
                        }
                    }}>
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
              <hr className="my-4" />
              <button className="btn btn-danger w-100 py-2 fw-bold" data-bs-toggle="modal" data-bs-target="#confirmSubmitModal">
                <i className="fa-solid fa-paper-plane me-2"></i>Complete Exam
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals remain exactly the same */}
      <div className="modal fade" id="calculatorModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered calc-modal">
          <div className="modal-content">
            <div className="modal-header py-2 bg-dark text-white">
              <h6 className="modal-title small">
                <i className="fa-solid fa-calculator me-1"></i> Calculator
              </h6>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body bg-light p-3">
              <div className="calc-screen" id="calcDisplay">0</div>
              <div className="row g-2">
                <div className="col-3"><button className="btn btn-secondary w-100 calc-btn" data-val="C">C</button></div>
                <div className="col-3"><button className="btn btn-secondary w-100 calc-btn" data-val="(">(</button></div>
                <div className="col-3"><button className="btn btn-secondary w-100 calc-btn" data-val=")">)</button></div>
                <div className="col-3"><button className="btn btn-warning w-100 calc-btn" data-val="/">÷</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="7">7</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="8">8</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="9">9</button></div>
                <div className="col-3"><button className="btn btn-warning w-100 calc-btn" data-val="*">×</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="4">4</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="5">5</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="6">6</button></div>
                <div className="col-3"><button className="btn btn-warning w-100 calc-btn" data-val="-">-</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="1">1</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="2">2</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val="3">3</button></div>
                <div className="col-3"><button className="btn btn-warning w-100 calc-btn" data-val="+">+</button></div>
                <div className="col-6"><button className="btn btn-light border w-100 calc-btn" data-val="0">0</button></div>
                <div className="col-3"><button className="btn btn-light border w-100 calc-btn" data-val=".">.</button></div>
                <div className="col-3"><button className="btn btn-success w-100 calc-btn" data-val="=">=</button></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="confirmSubmitModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-bold">
                <i className="fa-solid fa-triangle-exclamation text-warning me-2"></i>Submit Exam?
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body pt-2">
              <p className="mb-2">Are you sure you want to submit the exam?</p>
              <p className="text-muted small mb-0">Once submitted, you will not be able to change any of your answers.</p>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">No, Continue Exam</button>
              <button type="button" className="btn btn-danger fw-bold" onClick={handleConfirmSubmit}>
                <i className="fa-solid fa-paper-plane me-2"></i>Yes, Submit Exam
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}